import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { trackEvent, trackPageView } from '../utils/gt';

const API_URL = import.meta.env.VITE_GRAPHQL_URL.replace('/graphql', '');

export const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const code = searchParams.get('code');

  const schema = yup.object({
    password: yup
      .string()
      .min(6, t('forms.validations.pwd'))
      .required(t('forms.validations.pwdReq')),
    passwordConfirmation: yup
      .string()
      .min(6, t('forms.validations.pwd'))
      .required(t('forms.validations.pwdReq')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    trackPageView('reset_password', `${t('appUrl')}/reset-password`, '/reset-password');
  }, [t]);

  const onSubmit = async (data) => {
    if (data.password !== data.passwordConfirmation) {
      return toast.error(t('notifications.passwordsMismatch'));
    }

    trackEvent('reset_password_submit', 'Reset Password - Submit', 'reset_password', true);

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          password: data.password,
          passwordConfirmation: data.passwordConfirmation,
        }),
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        toast.error(t('notifications.resetPasswordError'));
      }
    } catch {
      toast.error(t('notifications.resetPasswordError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-root mt-root--page mt-auth-page">
      <div className="mt-auth-card">
        {!code ? (
          <div className="mt-auth-card__done">
            <h1 className="mt-auth-card__title">{t('pages.ResetPassword.invalidLink')}</h1>
            <p className="mt-auth-card__text">{t('pages.ResetPassword.invalidLinkDesc')}</p>
            <button type="button" className="mt-btn mt-btn--primary mt-auth-card__submit" onClick={() => navigate('/login', { state: { quiet: true } })}>
              {t('forms.buttons.goToLogin')}
            </button>
          </div>
        ) : isSuccess ? (
          <div className="mt-auth-card__done">
            <span className="mt-session-done__badge" aria-hidden="true">
              <Check />
            </span>
            <h1 className="mt-auth-card__title">{t('notifications.resetPasswordSuccessTitle')}</h1>
            <p className="mt-auth-card__text">{t('notifications.resetPasswordSuccessDesc')}</p>
            <button type="button" className="mt-btn mt-btn--primary mt-auth-card__submit" onClick={() => navigate('/login', { state: { quiet: true } })}>
              {t('forms.buttons.goToLogin')}
            </button>
          </div>
        ) : (
          <>
            <h1 className="mt-auth-card__title">{t('pages.ResetPassword.title')}</h1>
            <p className="mt-auth-card__text">{t('pages.ResetPassword.subtitle')}</p>
            <form className="mt-auth-card__form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <label className="mt-form-field">
                <span className="mt-form-field__label">{t('forms.buttons.newPassword')}</span>
                <input
                  {...register('password')}
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  aria-invalid={!!errors?.password}
                />
                {errors?.password?.message && <span className="mt-form-field__error">{errors.password.message}</span>}
              </label>
              <label className="mt-form-field">
                <span className="mt-form-field__label">{t('forms.buttons.confirmPassword')}</span>
                <input
                  {...register('passwordConfirmation')}
                  type="password"
                  autoComplete="new-password"
                  aria-invalid={!!errors?.passwordConfirmation}
                />
                {errors?.passwordConfirmation?.message && (
                  <span className="mt-form-field__error">{errors.passwordConfirmation.message}</span>
                )}
              </label>
              <button type="submit" className="mt-btn mt-btn--primary mt-auth-card__submit" disabled={isLoading}>
                {t('forms.buttons.resetPassword')}
              </button>
              <button type="button" className="mt-btn mt-btn--quiet mt-auth-card__secondary" onClick={() => navigate('/login', { state: { quiet: true } })}>
                {t('forms.buttons.backToLogin')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
